/**
 * Helper utilities for parameter extraction and normalization
 */

/**
 * Safely extract scalar value from parameter value
 * Handles: primitives, objects with label/value/amount, nested structures
 */
export function extractScalar(value: unknown): unknown {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
  if (Array.isArray(value)) return '';
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (obj.label != null && obj.label !== '') return obj.label;
    if (obj.value != null && obj.value !== '') return extractScalar(obj.value);
    if (obj.amount != null && obj.amount !== '') return extractScalar(obj.amount);
    if (obj.input != null && obj.input !== '') return extractScalar(obj.input);
  }
  return '';
}

/**
 * Safely extract units from parameter value
 * Checks: top-level units, nested value.units, nested amount.units
 */
export function extractUnits(value: unknown): string | undefined {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const v = value as Record<string, unknown>;
  
  if (typeof v.units === 'string' && v.units.trim() !== '') return v.units;
  
  if (v.value && typeof v.value === 'object') {
    const nested = v.value as Record<string, unknown>;
    if (typeof nested.units === 'string' && nested.units.trim() !== '') return nested.units;
  }
  
  if (v.amount && typeof v.amount === 'object') {
    const nested = v.amount as Record<string, unknown>;
    if (typeof nested.units === 'string' && nested.units.trim() !== '') return nested.units;
  }
  
  return undefined;
}

/**
 * Extract array/table rows from various storage formats
 */
export function extractRows(value: unknown): Array<Record<string, unknown>> {
  if (Array.isArray(value)) return value as Array<Record<string, unknown>>;
  if (!value || typeof value !== 'object') return [];
  
  const obj = value as Record<string, unknown>;
  const rowSources = [
    obj.tableRows,
    obj.gradientRows,
    obj.rows,
    obj.data,
    obj.GradientProgram,
    obj.value,
  ];
  
  for (const source of rowSources) {
    if (Array.isArray(source)) {
      return source as Array<Record<string, unknown>>;
    }
  }
  
  return [];
}

/**
 * Check if a parameter value is effectively empty
 */
export function isParamEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (typeof value === 'number') return false;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') {
    const v = value as Record<string, unknown>;
    
    if ('parameters' in v) return isParamEmpty(v.parameters);
    
    // Range check
    if ('minValue' in v || 'maxValue' in v || 'min' in v || 'max' in v) {
      const min = v.minValue ?? v.min;
      const max = v.maxValue ?? v.max;
      return (min == null || String(min).trim() === '') && 
             (max == null || String(max).trim() === '');
    }
    
    if ('amount' in v) return v.amount == null || String(v.amount).trim() === '';
    if ('value' in v) return isParamEmpty(v.value);
  }
  
  return false;
}

/**
 * Parse required/mandatory flags from various sources
 */
export function isRequired(required: unknown): boolean {
  if (required === true || required === 1) return true;
  if (typeof required === 'string') {
    const r = required.trim().toLowerCase();
    return ['mandatory', 'required', 'true', '1'].includes(r);
  }
  return false;
}

/**
 * Convert camelCase parameter name to readable label
 */
export function toLabel(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .toLowerCase()
    .trim();
}

/**
 * Check if parameter definition indicates a field type
 */
export function isFieldType(paramDef: any, fieldType: string): boolean {
  return String(paramDef?.fieldType || '').toLowerCase() === fieldType;
}

/**
 * Get sort weight for parameter (for ordering when no definition provided)
 */
export function getSortWeight(value: unknown): number {
  const fieldType = detectFieldType(value);
  if (fieldType === 'range') return 4;
  if (extractRows(value).length > 0) return 3;
  if (value && typeof value === 'object' && !Array.isArray(value)) return 2;
  return 1;
}

/**
 * Detect field type from raw value
 */
export function detectFieldType(value: unknown): string {
  if (value == null) return 'text';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'text';
  if (Array.isArray(value)) return 'text';
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if ('tableRows' in obj || 'rows' in obj || 'data' in obj) return 'text';
    if ('gradientRows' in obj || 'GradientProgram' in obj) return 'text';
    if (['minValue', 'maxValue', 'min', 'max', 'from', 'to'].some(k => k in obj)) return 'range';
    if ('from' in obj && 'to' in obj) return 'range';
    if ('amount' in obj && 'units' in obj) return 'number';
    if ('amount' in obj) return 'number';
  }
  return 'text';
}
